import { createFeatureSelector, createReducer, createSelector, on, select } from "@ngrx/store";
import * as adminActions from "./admin.actions";
import { IBook, IChapter, IPagesResponse, IPage, ILayoutList, IEditorChaptersResponse } from "../../models/admin";
import { AsyncState } from "..";
import { IOrderInfo } from "../../models/Subscription";
import { IErrors } from "../../models/error";

export interface IAdminState {
    Books: IBook[] | null,
    chapters: IChapter[] | null,
    pages: IPage[] | null,
    currentBookId: string | null,
    currentChapterId: string | null,
    currentParentChatperId: string | null,
    currentPageId: string | null,
    layouts: ILayoutList[] | null,
    subchapters: { [chapterId: string]: IChapter[] }
    chapterPages: { [chapterId: string]: IPage[] },
    Orders: {
        state: AsyncState,
        orders: { [key: string]: IOrderInfo[] },  // Key will be "${pageNumber}_${pageSize}_${orderStatus}_${paymentStatus}_${search}_${planId}"
        pagination: {
            pageNumber: number;
            pageSize: number;
            totalCount: number;
            totalPages: number;
        } | null;
    }
}

export const initialState: IAdminState = {
    Books: null,
    chapters: null,
    pages: null,
    currentBookId: null,
    currentChapterId: null,
    currentParentChatperId: null,
    currentPageId: null,
    layouts: null,
    subchapters: {},
    chapterPages: {},
    Orders: {
        state: { status: "NotFetched" },
        orders: {},
        pagination: null
    }
}

export const adminReducer = createReducer<IAdminState>(
    initialState,
    on(adminActions.GetEditorBooksSuccessAction, (state: IAdminState, props: { response: IPagesResponse<IBook> }) => {
        return {
            ...state,
            currentBookId: null,
            currentChapterId: null,
            currentParentChatperId: null,
            currentPageId: null,
            Books: props.response.Value
        }
    }),
    on(adminActions.GetEditorChaptersSuccessAction, (state, { parentId, chapters }) => {
        return {
            ...state,
            currentBookId: parentId,
            currentChapterId: null,
            currentParentChatperId: null,
            currentPageId: null,
            chapters: [...chapters.map(chapter => ({ ...chapter }))]
        }
    }),
    on(adminActions.DeleteChapterSuccessAction, (state, { chapterId }) => {
        return {
            ...state,
            currentChapterId: null,
            currentPageId: null,
            chapters: state.chapters?.filter(chapter => chapter.ChapterId !== chapterId) || []
        }
    }),
    on(adminActions.GetEditorChaptersFailureAction, (state) => ({
        ...state,
        chapters: []
    })),
    on(adminActions.GetEditorPagesSuccessAction, (state, { chapterId, pages }) => {
        let oldPages = state.pages || [];
        let newPages = oldPages.filter(page => page.chapterId !== chapterId);
        return {
            ...state,
            currentChapterId: chapterId,
            currentPageId: null,
            pages: [...newPages, ...pages.map(page => ({ ...page, chapterId }))],
            chapterPages: {
                ...state.chapterPages,
                [chapterId]: pages
            }
        }
    }),
    on(adminActions.GetEditorPagesFailureAction, (state) => ({
        ...state,
        pages: []
    })),
    on(adminActions.AddPageSuccessAction, (state, { response }) => {
        if (!state.pages) return state;
        // add the chapter Id to response because it does not come from server
        const page: IPage = {
            ...response.Value,
            chapterId: response.chapterId
        }
        return {
            ...state,
            currentPageId: page.PageId,
            pages: [...state.pages, { ...page }]
        };
    }),
    on(adminActions.AddPageFailureAction, (state) => {
        return state; // Just return current state on failure
    }),
    on(adminActions.DeletePageSuccessAction, (state, { pageId }) => {
        if (!state.pages) return state;
        return {
            ...state,
            currentPageId: null,
            pages: state.pages.filter(page => page.PageId !== pageId)
        };
    }),
    on(adminActions.DeletePageFailureAction, (state) => {
        return state; // Keep current state on failure
    }),
    //layout related reducers
    on(adminActions.GetLayoutsSuccessAction, (state, { layouts }) => {
        return {
            ...state,
            layouts: [...layouts], // Ensure we always have an array, even if empty
        }
    }),


    on(adminActions.DeleteLayoutSuccessAction, (state, { layoutId }) => {
        return {
            ...state,
            layouts: state.layouts?.filter(layout => layout.LayoutId !== layoutId) || null
        }
    }),
    on(adminActions.GetLayoutsFailureAction, (state) => ({
        ...state,
        layouts: [], // Set to empty array on failure instead of null
    })),

    on(adminActions.AddGlobalLayoutSuccessAction, (state, { layout }) => {
        const newLayout: ILayoutList = {
            Cols: layout.Cols,
            Rows: layout.Rows,
            IsGlobal: true,
            LayoutId: layout.LayoutId ?? "0", // we are sure that we will recieve the layout id from the server
            Name: layout.Name,
        }
        return {
            ...state,
            layouts: state.layouts ? [...state.layouts, layout] : [newLayout]
        }
    }),

    on(adminActions.FetchSubChaptersSuccesAction, (state, props: { response: IEditorChaptersResponse, chapterId: string }) => {
        return {
            ...state,
            subchapters: {
                ...state.subchapters,
                [props.chapterId]: props.response.Value
            }
        };
    }),
    // Fetch Orders
    on(adminActions.AdminFetchOrdersAction, (state: IAdminState) => {
        return {
            ...state,
            Orders: {
                ...state.Orders,
                state: { status: 'Loading' }
            }
        };
    }),
    on(adminActions.AdminFetchOrdersSuccessAction, (state: IAdminState, { response, paymentStatus, orderStatus, search, planid }) => {
        const key = `${response.PageNumber}_${response.PageSize}_${orderStatus || 'all'}_${paymentStatus || 'all'}_${search || 'all'}_${planid || 'all'}`;
        return {
            ...state,
            Orders: {
                ...state.Orders,
                state: { status: 'Success' },
                orders: {
                    ...state.Orders.orders,
                    [key]: response.Value || []
                },
                pagination: {
                    pageNumber: response.PageNumber,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount,
                    totalPages: response.TotalPages
                }
            }
        };
    }),
    on(adminActions.AdminFetchOrdersFailureAction, (state: IAdminState, props: { error: IErrors }) => {
        return {
            ...state,
            Orders: {
                ...state.Orders,
                state: { status: 'Failure', error: props?.error?.Errors?.[0]?.Message || 'Failed to load Orders' }
            }
        };
    }),
    on(adminActions.AdminClearOrdersStateAction, (state: IAdminState) => {
        return {
            ...state,
            Orders: {
                state: { status: 'NotFetched' },
                orders: {},
                pagination: null
            }
        };
    }),
    on(adminActions.AdminUpdateOrdersPaginationnAction, (state: IAdminState, { pageNumber, pageSize }) => {
        return {
            ...state,
            Orders: {
                ...state.Orders,
                pagination: {
                    pageNumber,
                    pageSize,
                    totalCount: state.Orders.pagination?.totalCount ?? 0,
                    totalPages: state.Orders.pagination?.totalPages ?? 0
                }
            }
        };
    })
);

const selectAdminState = createFeatureSelector<IAdminState>("admin");

export const selectEditorBooks = createSelector(
    selectAdminState,
    (state: IAdminState) => state.Books
);

export const selectEditorChapters = createSelector(
    selectAdminState,
    (state: IAdminState) => [state.currentBookId, state.chapters]
);

export const selectEditorPages = createSelector(
    selectAdminState,
    (state: IAdminState) => [state.currentChapterId, state.pages]
);
export const selectEditorCurrentBookId = createSelector(
    selectAdminState,
    (state: IAdminState) => state.currentBookId
);
export const selectEditorCurrentChapterId = createSelector(
    selectAdminState,
    (state: IAdminState) => state.currentChapterId
);
export const selectEditorCurrentParentChapterId = createSelector(
    selectAdminState,
    (state: IAdminState) => state.currentParentChatperId
);
export const selectEditorCurrentPageId = createSelector(
    selectAdminState,
    (state: IAdminState) => state.currentPageId
);

export const selectLayouts = createSelector(
    selectAdminState,
    (state: IAdminState) => state.layouts
);

export const selectSubChaptersById = (chapterId: string) =>
    createSelector(selectAdminState, (state: IAdminState | undefined) =>
        state && state.subchapters ? state.subchapters[chapterId] ?? null : null
    );

export const selectPagesByChapterId = (chapterId: string) =>
    createSelector(selectAdminState, (state: IAdminState | undefined) =>
        state && state.chapterPages ? state.chapterPages[chapterId] ?? null : null
    );

// Selector for orders on a specific page with filters
export const adminSelectOrdersByPageAndFilters = (pageNumber: number, pageSize: number, orderStatus?: string, paymentStatus?: string, search?: string, planId?: string) =>
    createSelector(selectAdminState, (state: IAdminState) => {
        const key = `${pageNumber}_${pageSize}_${orderStatus || 'all'}_${paymentStatus || 'all'}_${search || 'all'}_${planId || 'all'}`;
        return state.Orders.orders[key] || [];
    });
// Selector for orders pagination metadata
export const adminSelectOrdersPagination = createSelector(
    selectAdminState,
    (state: IAdminState) => state.Orders.pagination
);

// Selector for orders async state
export const adminSelectOrdersState = createSelector(
    selectAdminState,
    (state: IAdminState) => state.Orders.state
);

// Selector for all orders (if needed)
export const adminSelectAllOrders = createSelector(
    selectAdminState,
    (state: IAdminState) => {
        return Object.values(state.Orders.orders).flat();
    }
);
// Selector For Getting the Billing History Pagination State
export const adminSelectBillingHistoryPaingation = createSelector(
    selectAdminState,
    (state: IAdminState) => {
        return state.Orders.pagination
    }
)